import { useState, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { agentPermissionsApi } from "../services/api";
import { useAgentPermissions, AGENT_PERMS_ME_KEY } from "../context/AgentPermissionsContext";
import RoleSurface from "../components/designSystem/RoleSurface";
import { FOCUS_RING_CLS, TOUCH_TARGET_CLS } from "../components/designSystem/designTokens";

const PERMISSIONS = [
  { key: "agents_view",    label: "צפייה",   desc: "צפייה בסוכנים ובריצות AI" },
  { key: "agents_run",     label: "הפעלה",  desc: "הפעלה ידנית של ניתוח AI" },
  { key: "agents_approve", label: "אישור",   desc: "אישור / דחייה / משוב על ניתוח" },
  { key: "agents_manage",  label: "ניהול",  desc: "יצירה, עריכה, ניהול סוכנים והרשאות" },
];

function PermissionToggle({
  userId,
  permission,
  permissionLabel,
  userName,
  checked,
  disabled,
  disabledReason,
  pending,
  onChange,
}) {
  // A switch used to have no accessible name beyond its hover title ("לחץ להענקה" / "לחץ לביטול"),
  // so a screen reader heard the same four words on every cell and never the user or the
  // permission. The name now carries the permission, the user and the current state; a disabled
  // switch says it cannot be changed rather than inviting a click it would refuse.
  const stateText = checked ? "מוענקת" : "אינה מוענקת";
  const changeText = disabled
    ? disabledReason || "אינה ניתנת לשינוי"
    : pending
    ? "מעדכן…"
    : checked
    ? "לחץ לביטול"
    : "לחץ להענקה";

  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={`${permissionLabel} — ${userName}: ${stateText}; ${changeText}`}
      disabled={disabled || pending}
      onClick={() => onChange(userId, permission, !checked)}
      title={changeText}
      className={`${TOUCH_TARGET_CLS} ${FOCUS_RING_CLS} relative inline-flex items-center justify-center rounded-full ${
        disabled  ? "cursor-not-allowed opacity-40" :
        pending   ? "cursor-wait opacity-70" :
        "cursor-pointer"
      }`}
    >
      <span
        aria-hidden="true"
        className={`relative inline-flex h-5 w-9 shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ${
          checked ? "bg-blue-600" : "bg-gray-300"
        }`}
      >
        <span
          className={`pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow transform ring-0 transition duration-200 ${
            checked ? "-translate-x-4" : "translate-x-0"
          }`}
        />
      </span>
    </button>
  );
}

export default function AgentPermissionsPage() {
  const { can, loading: permissionsLoading, failed: permissionsFailed } = useAgentPermissions();
  const queryClient = useQueryClient();
  const [errorMsg, setErrorMsg] = useState(null);
  // optimistic: { "userId:permission": true|false } — tracks in-flight changes
  const [optimistic, setOptimistic] = useState({});

  const { data: users = [], isLoading, isError: usersFailed } = useQuery({
    queryKey: ["agent-permissions-users"],
    queryFn: () => agentPermissionsApi.users(),
    staleTime: 10_000,
  });

  const settle = useCallback((userId, permission) => {
    setOptimistic((prev) => {
      const next = { ...prev };
      delete next[`${userId}:${permission}`];
      return next;
    });
  }, []);

  const invalidateAll = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["agent-permissions-users"] });
    // re-fetch caller's own permission set so canAgent() updates immediately
    queryClient.invalidateQueries({ queryKey: [AGENT_PERMS_ME_KEY] });
  }, [queryClient]);

  const grant = useMutation({
    mutationFn: ({ user_id, permission }) => agentPermissionsApi.grant(user_id, permission),
    onSuccess: (_data, { user_id, permission }) => {
      settle(user_id, permission);
      invalidateAll();
      setErrorMsg(null);
    },
    onError: (err, { user_id, permission }) => {
      settle(user_id, permission);
      setErrorMsg(err?.response?.data?.detail || "שגיאה בשמירת ההרשאה");
    },
  });

  const revoke = useMutation({
    mutationFn: ({ user_id, permission }) => agentPermissionsApi.revoke(user_id, permission),
    onSuccess: (_data, { user_id, permission }) => {
      settle(user_id, permission);
      invalidateAll();
      setErrorMsg(null);
    },
    onError: (err, { user_id, permission }) => {
      settle(user_id, permission);
      setErrorMsg(err?.response?.data?.detail || "שגיאה בביטול ההרשאה");
    },
  });

  const handleToggle = (userId, permission, newValue) => {
    setOptimistic((prev) => ({ ...prev, [`${userId}:${permission}`]: newValue }));
    if (newValue) {
      grant.mutate({ user_id: userId, permission });
    } else {
      revoke.mutate({ user_id: userId, permission });
    }
  };

  // A permission load that is still in flight or failed is not "no permission": a permitted user saw the denial on every hard reload.
  if (permissionsLoading) {
    return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" /></div>;
  }
  if (permissionsFailed) {
    return <div role="alert" className="p-8 text-center text-red-600 text-sm">לא ניתן לטעון את ההרשאות שלך. נסה לרענן את הדף.</div>;
  }

  if (!can("agents_manage")) {
    return (
      <div className="p-8 text-center text-gray-500">
        אין לך הרשאה לנהל הרשאות AI.
      </div>
    );
  }

  return (
    <RoleSurface
      role="admin"
      dir="rtl"
      width="narrow"
      padding="page"
      title="הרשאות AI"
      description="הגדר מי יכול לצפות, להפעיל, לאשר ולנהל סוכני AI. בעלי המערכת (Owner) מקבלים את כל ההרשאות אוטומטית. מנהלים (Admin) יכולים לנהל הרשאות; גישה תפעולית דורשת הענקה מפורשת."
      testId="agent-permissions-role-surface"
    >

      {errorMsg && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {errorMsg}
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-right px-4 py-3 font-semibold text-gray-700 w-64">משתמש</th>
                {PERMISSIONS.map((p) => (
                  <th key={p.key} className="text-center px-3 py-3 font-semibold text-gray-700">
                    <div>{p.label}</div>
                    <div className="font-normal text-xs text-gray-400 mt-0.5">{p.desc}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map((user, idx) => {
                const isOwner = user.role === "owner";
                const isAdmin = user.role === "admin";
                return (
                  <tr
                    key={user.id}
                    className={`border-b border-gray-100 ${idx % 2 === 0 ? "bg-white" : "bg-gray-50/50"}`}
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900 truncate max-w-[14rem]">
                        {user.display_name}
                      </div>
                      <div className="text-xs text-gray-400 truncate max-w-[14rem]">{user.email}</div>
                      {isOwner && (
                        <span className="inline-block mt-0.5 text-[10px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full font-medium">
                          Owner
                        </span>
                      )}
                      {isAdmin && (
                        <span className="inline-block mt-0.5 text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full font-medium">
                          Admin
                        </span>
                      )}
                    </td>
                    {PERMISSIONS.map((p) => {
                      const optKey = `${user.id}:${p.key}`;
                      const isImplicit = isAdmin && p.key === "agents_manage";
                      const checked = optKey in optimistic
                        ? optimistic[optKey]
                        : user.agent_permissions.includes(p.key);
                      const pendingThisToggle = optKey in optimistic;
                      return (
                        <td key={p.key} className="px-3 py-3 text-center">
                          <div className="flex justify-center">
                            <PermissionToggle
                              userId={user.id}
                              permission={p.key}
                              permissionLabel={p.label}
                              userName={user.display_name}
                              checked={checked}
                              disabled={isOwner || isImplicit}
                              disabledReason={
                                isOwner
                                  ? "בעל המערכת מקבל את כל ההרשאות אוטומטית"
                                  : isImplicit
                                  ? "מנהל מקבל הרשאת ניהול אוטומטית"
                                  : null
                              }
                              pending={pendingThisToggle}
                              onChange={handleToggle}
                            />
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
              {users.length === 0 && (
                <tr>
                  <td colSpan={PERMISSIONS.length + 1}
                      role={usersFailed ? "alert" : undefined}
                      className={`text-center py-10 ${usersFailed ? "text-red-600" : "text-gray-400"}`}>
                    {usersFailed ? "לא ניתן לטעון את רשימת המשתמשים וההרשאות" : "לא נמצאו משתמשים"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </RoleSurface>
  );
}
